import React from "react";
import { Stack, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";
import dayjs from "dayjs";

import { PagePrint } from "../PagePrint";
import { PageMarker } from "../PageMarker";
import { FOUNDRIES } from "../../constants";
import { QUOTE_NOTES } from "../../constants/quoteTerms";
import { getDollarValue } from "../../helpers/currency";

import Header from "../VendorTerms/Header.png";
import LogoBackground from "../VendorTerms/LogoBackground.png";

import QuoteTermsPage from "./QuoteTermsPage";
import {
  StyledHeader,
  StyledWatermark,
  StyledPageContent,
  StyledTitle,
  StyledLabel,
  StyledBoldLabel,
  StyledInfoGrid,
  StyledPricingRow,
  StyledDeliveryGrid,
  StyledGuarantee,
} from "./styles";

const QuotePage = ({ children }) => (
  <Stack width="100%" height="100%">
    {children}
    <StyledHeader component="img" src={Header} />
    <StyledWatermark component="img" src={LogoBackground} />
  </Stack>
);

export const CustomerQuoteDocument = () => {
  const { watch } = useFormContext();

  const locationId = watch("locationId");
  const foundry = FOUNDRIES.find((f) => f.id === locationId) || FOUNDRIES[0];
  const quoteNumber = watch("quoteNumber") || "—";
  const createdAt = watch("createdAt");
  const date = createdAt
    ? dayjs(createdAt).format("M/DD/YY")
    : dayjs().format("M/DD/YY");

  // Customer info
  const customerName = watch("customerName") || "";
  const contactName = watch("contactName") || "";
  const customerAddress = watch("customerAddress") || {};
  const contactEmail = watch("contactEmail") || "";

  // Part info
  const partNumber = watch("partNumber") || "";
  const partName = watch("partName") || "";
  const revision = watch("revision") || "-";
  const materialName = watch("materialName") || watch("alloy") || "";

  // Pricing
  const price = watch("price");
  const quantity = watch("quantity") || 0;
  const nreFee = watch("nreFee");
  const toolingCost = watch("toolingCost");
  const toolingDescription = watch("toolingDescription") || "";
  const setupCharge = watch("setupCharge");

  // Testing
  const mechPropCost = watch("mechPropCost");
  const chemAnaCost = watch("chemAnaCost");

  // Delivery
  const toolingLeadTime = watch("toolingLeadTime") || "11-14";
  const firstArticleLeadTime = watch("firstArticleLeadTime") || "5-7";
  const productionLeadTime = watch("productionLeadTime") || "9-11";

  const fobLabel = `IPC Foundry Group ${foundry?.address?.state || ""}`;

  return (
    <Stack gap="25px">
      <PagePrint cardProps={{ id: "page-2", sx: { position: "relative" } }}>
        <QuotePage>
          <StyledPageContent>
            <StyledTitle>IPC Foundry Group Quotation</StyledTitle>

            {/* Customer + Date/Ref */}
            <StyledInfoGrid>
              <Stack gap="2px">
                {customerName && (
                  <StyledBoldLabel>{customerName}</StyledBoldLabel>
                )}
                {contactName && <StyledLabel>Attn. {contactName}</StyledLabel>}
                {customerAddress?.street && (
                  <StyledLabel>{customerAddress.street}</StyledLabel>
                )}
                {customerAddress?.city && (
                  <StyledLabel>
                    {customerAddress.city}, {customerAddress.state}{" "}
                    {customerAddress.zip}
                  </StyledLabel>
                )}
                {contactEmail && <StyledLabel>{contactEmail}</StyledLabel>}
              </Stack>
              <Stack alignItems="flex-end" gap="2px">
                <StyledLabel>
                  Date:{" "}
                  <StyledBoldLabel component="span">{date}</StyledBoldLabel>
                </StyledLabel>
                <StyledLabel>
                  Quote Ref:{" "}
                  <StyledBoldLabel component="span">
                    {quoteNumber}
                  </StyledBoldLabel>
                </StyledLabel>
              </Stack>
            </StyledInfoGrid>

            {/* Part Info */}
            <Stack gap="4px">
              <StyledLabel>
                Part No:{" "}
                <StyledBoldLabel component="span">{partNumber}</StyledBoldLabel>
              </StyledLabel>
              <Stack direction="row" justifyContent="space-between">
                <StyledLabel>
                  Part Name:{" "}
                  <StyledBoldLabel component="span">{partName}</StyledBoldLabel>
                </StyledLabel>
                {(mechPropCost || chemAnaCost) && (
                  <Stack alignItems="flex-end">
                    <StyledBoldLabel sx={{ textDecoration: "underline" }}>
                      If Required:
                    </StyledBoldLabel>
                    {mechPropCost && (
                      <StyledLabel>
                        Mechanical Properties:{" "}
                        <StyledBoldLabel component="span">
                          {getDollarValue(mechPropCost)} ea.
                        </StyledBoldLabel>
                      </StyledLabel>
                    )}
                    {chemAnaCost && (
                      <StyledLabel>
                        Disc & Chemical Ana:{" "}
                        <StyledBoldLabel component="span">
                          {getDollarValue(chemAnaCost)} ea.
                        </StyledBoldLabel>
                      </StyledLabel>
                    )}
                  </Stack>
                )}
              </Stack>
              <StyledLabel>
                Rev:{" "}
                <StyledBoldLabel component="span">{revision}</StyledBoldLabel>
              </StyledLabel>
              {materialName && (
                <StyledLabel>
                  Alloy:{" "}
                  <StyledBoldLabel component="span">
                    {materialName}
                  </StyledBoldLabel>
                </StyledLabel>
              )}
            </Stack>

            {/* Pricing */}
            <Stack gap="6px">
              <StyledPricingRow highlight>
                <StyledBoldLabel sx={{ minWidth: 130 }}>
                  Quantity Price:
                </StyledBoldLabel>
                <Stack direction="row" gap="8px" alignItems="baseline">
                  <StyledBoldLabel>{quantity}</StyledBoldLabel>
                  <StyledLabel>Production</StyledLabel>
                  <StyledLabel>PCS</StyledLabel>
                  <StyledLabel>@</StyledLabel>
                  <StyledBoldLabel>{getDollarValue(price)} ea.</StyledBoldLabel>
                </Stack>
              </StyledPricingRow>
              {nreFee && (
                <StyledPricingRow>
                  <StyledBoldLabel sx={{ minWidth: 130 }} />
                  <Stack direction="row" gap="8px" alignItems="baseline">
                    <StyledBoldLabel>1</StyledBoldLabel>
                    <StyledLabel>NRE Fee</StyledLabel>
                    <StyledLabel>PCS</StyledLabel>
                    <StyledLabel>@</StyledLabel>
                    <StyledBoldLabel>
                      {getDollarValue(nreFee)} ea.
                    </StyledBoldLabel>
                  </Stack>
                </StyledPricingRow>
              )}
              {toolingCost && (
                <StyledPricingRow>
                  <StyledBoldLabel sx={{ minWidth: 130 }} />
                  <Stack direction="row" gap="8px" alignItems="baseline">
                    <StyledLabel>Tooling</StyledLabel>
                    <StyledLabel>@</StyledLabel>
                    <StyledBoldLabel>
                      {getDollarValue(toolingCost)}
                      {toolingDescription ? ` (${toolingDescription})` : ""}
                    </StyledBoldLabel>
                  </Stack>
                </StyledPricingRow>
              )}
            </Stack>

            {/* FOB / Setup */}
            <Stack gap="4px">
              <StyledLabel>
                <StyledBoldLabel component="span">FOB</StyledBoldLabel>:{" "}
                {fobLabel}
              </StyledLabel>
              {setupCharge && (
                <StyledLabel>
                  <StyledBoldLabel component="span">
                    Set-up Charge
                  </StyledBoldLabel>
                  : {getDollarValue(setupCharge)} - One Time Only
                </StyledLabel>
              )}
            </Stack>

            {/* Delivery */}
            <Stack gap="4px">
              <StyledBoldLabel>Delivery:</StyledBoldLabel>
              <StyledDeliveryGrid>
                <StyledLabel>Tooling</StyledLabel>
                <StyledLabel>{toolingLeadTime}</StyledLabel>
                <StyledLabel>weeks after receipt PO</StyledLabel>

                <StyledLabel>First Articles</StyledLabel>
                <StyledLabel>{firstArticleLeadTime}</StyledLabel>
                <StyledLabel>weeks after receipt of tooling</StyledLabel>

                <StyledLabel>Production</StyledLabel>
                <StyledLabel>{productionLeadTime}</StyledLabel>
                <StyledLabel>
                  weeks after approval of first articles
                </StyledLabel>
              </StyledDeliveryGrid>
            </Stack>

            {/* Lead time disclaimer */}
            <Stack alignItems="center" gap="2px">
              <Typography
                variant="caption"
                fontSize={11}
                fontWeight={700}
                textAlign="center"
              >
                Lead times are approximate and will be determined based on
                backlog at time PO is issued.
              </Typography>
              <Typography
                variant="caption"
                fontSize={11}
                fontWeight={700}
                textAlign="center"
              >
                IPC can ship up to 1-10% over order quantity.
              </Typography>
            </Stack>

            {/* Notes */}
            <Stack gap="2px" pl="24px">
              {QUOTE_NOTES.map((note, i) => (
                <StyledLabel key={i}>
                  <StyledBoldLabel component="span">{i + 1}.</StyledBoldLabel>{" "}
                  {note}
                </StyledLabel>
              ))}
            </Stack>

            <StyledGuarantee>
              Estimation Guaranteed for 30 days.
            </StyledGuarantee>
          </StyledPageContent>
          <PageMarker page={1} pages={2} />
        </QuotePage>
      </PagePrint>

      {/* Terms & Conditions Page */}
      <QuoteTermsPage />
    </Stack>
  );
};

export default CustomerQuoteDocument;
